// All bot-bubble copy + path-aware wordings, ported from the monolith. The proto
// wording IS the spec (INSTALL.md §2/§4) — keep exact. Dynamic builders take the
// minimal state they need so they stay pure and testable.

// ── Step intros (bot bubbles shown before the step's choice card) ──
export const UNIVERSE_INTRO: string[] = [
  'Bienvenue chez Nounou-Top. Quelques questions rapides pour vous orienter.',
  'Que recherchez-vous aujourd\'hui ?',
]
export const MP_ROLE_INTRO = 'Vous êtes plutôt...'
export const MP_CARETYPE_INTRO = 'Quel type de garde recherchez-vous ?'
export const MN_SUBPROFILE_INTRO = 'Précisez s\'il vous plaît.'

// Address step copy differs between the marketplace zone and the MAM zone.
export function addressIntro(stepKey: string): string {
  return stepKey === 'mam-zone'
    ? 'Où se situe votre MAM ? J\'ai besoin de l\'adresse pour finaliser votre inscription.'
    : 'Pour vous proposer les meilleures annonces près de chez vous, indiquez ici votre adresse.'
}
// CTA label on the address card (marketplace vs MAM).
export function addressCtaLabel(stepKey: string): string {
  return stepKey === 'mam-zone' ? 'Continuer →' : 'C\'est parti'
}

// mn-role intro is universe-aware.
export function mnRoleIntro(universe: string | null | undefined): string {
  return universe === 'notebook'
    ? 'Pour partager le quotidien des enfants, vous êtes plutôt...'
    : 'Pour vous aider à gérer vos contrats et fiches de paie, vous êtes plutôt...'
}

// Long-form intro per sub-profile.
const LONGFORM_INTRO_BY_PROFILE: Record<string, string> = {
  mam: 'Pour finaliser votre inscription, j\'ai besoin de quelques infos sur votre MAM.',
  rpe: 'Pour finaliser votre inscription, j\'ai besoin de quelques infos sur votre RPE.',
  trainer: 'Pour finaliser votre inscription, j\'ai besoin de quelques infos sur votre organisme.',
  agency: 'Pour finaliser votre inscription, j\'ai besoin de quelques infos sur votre agence.',
}
export function longformIntro(subProfile: string | null | undefined): string {
  return (subProfile && LONGFORM_INTRO_BY_PROFILE[subProfile]) || 'Pour finaliser votre inscription...'
}

// ── Signup sub-phase copy ───────────────────────────────────────
export const EMAIL_FORM_INTRO = 'Parfait, donnez-moi votre email et choisissez un mot de passe.'
export const EMAIL_ACCOUNT_CREATED = 'C\'est fait, votre compte est créé.'
// Bot bubble when the browser blocks the Google OAuth popup (window.open returned null) —
// shown before routing back to the auth-choice card so the visitor can retry.
export const GOOGLE_POPUP_BLOCKED = 'Autorisez la fenêtre Google pour continuer, puis réessayez.'
// Inline error on the email form when the address is already registered.
export const EMAIL_ALREADY_EXISTS = 'Un compte existe déjà avec cet email.'
// Bot bubble when account creation fails (server/validation error) — shown on the email
// submit, leaving the form open for a retry.
export const SIGNUP_FAILED = 'Oups, la création de votre compte a échoué. Merci de réessayer.'

// Below this many nearby counterparts the social-proof bubble drops the number: a small count
// reads as scarcity, not proof. confirmAddress skips the bubble entirely on a real 0.
export const AVAILABILITY_COUNT_FLOOR = 5

// Social-proof bubble after the marketplace address step (path-aware family/nounou).
export function availabilityBubble({ role, count }: { role?: string | null; count?: number | null }): string {
  const n = count ?? 0
  const forChildminder = role === 'childminder'
  if (n < AVAILABILITY_COUNT_FLOOR) {
    return forChildminder
      ? 'Des familles cherchent une nounou près de chez vous cette semaine !'
      : 'Des profils sont disponibles près de chez vous cette semaine !'
  }
  if (forChildminder) {
    return `Bonne nouvelle : <strong>${n} familles</strong> cherchent une nounou près de chez vous cette semaine !`
  }
  return `Bonne nouvelle : <strong>${n} profils</strong> sont disponibles près de chez vous cette semaine !`
}

// Signup intro bubble — the last bot turn before the consent card: path-aware benefit, then
// the CGUA link the consent tap refers to (BotBubble renders this trusted markup via v-html).
const SIGNUP_PURPOSE_BY_PATH: Record<string, string> = {
  marketplace_family: 'pour accéder aux annonces',
  marketplace_childminder: 'pour accéder aux annonces',
  management_employer: 'pour gérer vos contrats',
  management_employee: 'pour gérer votre activité',
  notebook_employer: 'pour partager le quotidien',
  notebook_employee: 'pour partager le quotidien',
}
const TERMS_LINK =
  '<a href="/terms-of-use" target="_blank" rel="noopener">Conditions générales d\'utilisation et d\'abonnement</a>'
export function signupIntro(key?: string | null): string {
  const purpose = key ? SIGNUP_PURPOSE_BY_PATH[key] : undefined
  const benefit = purpose ? ` ${purpose}` : ''
  return `Dernière étape : créer votre compte gratuit${benefit}.<br>Consultez les ${TERMS_LINK}.`
}

// ── A11y: only the current step's question is announced (aria-live) ──
export const STEP_ANNOUNCEMENTS: Record<string, string> = {
  universe: 'Étape 1 : que cherchez-vous aujourd\'hui ?',
  'mp-role': 'Étape : êtes-vous famille ou nounou ?',
  'mp-zone': 'Étape : où habitez-vous ?',
  'mp-caretype': 'Étape : quel type de garde recherchez-vous ?',
  'mn-role': 'Étape : quel est votre rôle ?',
  'mn-subprofile': 'Étape : précisez votre profil.',
  'mn-longform': 'Formulaire d\'inscription détaillé.',
  signup: 'Étape finale : créez votre compte.',
}
